import { Bell } from 'lucide-react';
import { useAuth } from '@/lib/auth';

interface MobileTopbarProps {
  /**
   * Quando omitido, exibe modo logo (Home): logotipo GestAKI + sino + avatar.
   * Quando preenchido, exibe eyebrow + título textual.
   */
  title?: string;
  subtitle?: string;
}

/** Iniciais a partir do nome do usuário */
function initials(name: string | undefined): string {
  if (!name) return 'GK';
  const parts = name.split(' ').filter(Boolean).slice(0, 2);
  if (parts.length === 0) return 'GK';
  return parts.map((s) => (s[0] ?? '').toUpperCase()).join('');
}

/**
 * Topbar reutilizável nos módulos mobile.
 *
 * Sem `title` → modo Home: logotipo textual + sino + avatar do usuário logado.
 * Com `title`  → modo módulo: eyebrow opcional + título principal.
 *
 * Touch targets: botão sino 44px, avatar 44px.
 */
export function MobileTopbar({ title, subtitle }: MobileTopbarProps) {
  const { user } = useAuth();
  const userInitials = initials(user?.name);

  if (title) {
    /* Modo módulo — título + eyebrow */
    return (
      <div
        style={{
          padding: '4px 0 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          {subtitle && (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                fontWeight: 600,
                color: 'var(--fg-muted)',
                marginBottom: 2,
              }}
            >
              {subtitle}
            </div>
          )}
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontFamily: 'var(--font-display)',
              color: 'var(--fg)',
            }}
          >
            {title}
          </div>
        </div>
      </div>
    );
  }

  /* Modo Home — logo + sino + avatar */
  return (
    <div
      style={{
        padding: '4px 0 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Logotipo textual */}
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 18,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          color: 'var(--fg)',
        }}
      >
        Gest<span style={{ color: 'var(--mint-deep)' }}>AKI</span>
      </span>

      {/* Ações: sino + avatar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          aria-label="Notificações"
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--r-pill)',
            background: 'var(--card-soft)',
            border: '1px solid var(--card-stroke)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--fg-muted)',
            cursor: 'pointer',
          }}
        >
          <Bell size={17} strokeWidth={1.8} aria-hidden />
        </button>

        <button
          aria-label={`Perfil de ${user?.name ?? 'usuário'}`}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--clay-deep), #8E5736)',
            color: 'var(--clay-soft)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
            border: 'none',
          }}
        >
          {userInitials}
        </button>
      </div>
    </div>
  );
}
